/** @jsxImportSource @emotion/react */
import { useMemo } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import CopyToClipboard from 'react-copy-to-clipboard'
import { Button, ButtonExtendedProps, Text } from 'grommet'
import { Link as LinkIcon, StatusGood } from 'grommet-icons'

import { plotUrl } from 'util/plot'
import useCopy from 'hooks/useCopy'
import useDeviceSize from 'hooks/useDevice'

type Props = {
  shortCode: string
  hideLabel?: boolean
  showResults?: boolean
} & Omit<ButtonExtendedProps, 'onClick'>
export default function CopyUrlButton({
  shortCode,
  hideLabel = false,
  showResults = false,
  ...rest
}: Props) {
  const { t } = useTranslation()
  const { copied, onCopy } = useCopy()
  const { locale, query } = useRouter()
  const { small, medium, fontSize } = useDeviceSize()
  const url = useMemo(
    () => plotUrl({ shortCode, showResults, locale, query }),
    [locale, query, shortCode, showResults],
  )
  return (
    <CopyToClipboard text={url} onCopy={onCopy}>
      <Button
        primary
        reverse
        href={url}
        color="white"
        fill="horizontal"
        css={{
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
        }}
        size={small ? 'large' : 'small'}
        onClick={(e) => e.preventDefault()}
        label={
          hideLabel && medium ? undefined : (
            <Text textAlign="center" size={fontSize} css={{ whiteSpace: 'nowrap' }}>
              {t('cta.copy-link')}
            </Text>
          )
        }
        icon={copied ? <StatusGood size="16px" /> : <LinkIcon size="16px" />}
        {...rest}
      />
    </CopyToClipboard>
  )
}
